import classnames from 'classnames';
import * as React from 'react';

import { Button } from '@ao-internal/components-react';
import * as digitalData from '@ao-internal/digital-data';

import SendMessageIcon from '../assets/icons/SendMessage';
import { TaggingEventNames } from '../globals';
import { getLastMessageGroupContainingAnIntent } from '../helpers/state';
import useChat from '../hooks/useChat';
import { InputTypes, useChatDispatch, useChatState } from '../store';

export function isInputValid(value: string): boolean {
	return value.trim().length === 0 ? false : true;
}

interface ChatFormProps {
	maxCharacterLength?: number;
	isReadOnly?: boolean;
}

export const ChatForm: React.FC<ChatFormProps> = ({
	maxCharacterLength,
	isReadOnly,
}) => {
	const { userInputValue, chatHistory } = useChatState();
	const { setUserInputValue } = useChatDispatch();
	const { sendMessage } = useChat();
	const [isFormValid, setIsFormValid] = React.useState(false);
	const inputRef = React.createRef<HTMLTextAreaElement>();

	function handleInputChange(evt: React.ChangeEvent<HTMLTextAreaElement>) {
		let inputValue = evt.target.value;

		if (maxCharacterLength && inputValue.length > maxCharacterLength) {
			inputValue = inputValue.slice(0, maxCharacterLength);
		}

		setUserInputValue(inputValue);
	}

	const handleSubmit = (evt: React.FormEvent | React.KeyboardEvent) => {
		evt.preventDefault();

		if (isFormValid) {
			const message = userInputValue.trim();
			const lastChatGroupWithIntent =
				getLastMessageGroupContainingAnIntent(chatHistory);

			digitalData.sendBeacon(TaggingEventNames.interaction, {
				type: 'submit',
				element: 'form',
				value: '',
			});

			sendMessage(message, {
				inputType: InputTypes.Text,
				groupIntent: lastChatGroupWithIntent
					? lastChatGroupWithIntent.intent
					: undefined,
			});
		}

		setUserInputValue('');
	};

	const handleInputKeyDown = (evt?: React.KeyboardEvent) => {
		// console.log('evt', evt);
		// console.log('evt.shiftKey', evt.shiftKey);
		// console.log('evt.keyCode', evt.keyCode);
		// Has Enter been pressed without the shitkey
		// if (evt.keyCode === 13 && shiftKeyActive === false) {
		// 	evt.preventDefault();
		// 	handleSubmit(evt);
		// }
		if (evt && evt.keyCode === 13 && evt.shiftKey === false) {
			evt.preventDefault();
			handleSubmit(evt);
		}
	};

	React.useEffect(() => {
		setIsFormValid(isInputValid(userInputValue));
		// has NO content, send typing = false
		// has content, has NOT been updated in the last X seconds, send typing = false
		// has content, has been updated in the last X seconds, send typing = true;
	}, [userInputValue]);

	return (
		<form
			ref={(node) => node && isReadOnly && node.setAttribute('inert', '')}
			data-tag-area="form"
			data-testid="chat-form"
			className="relative flex justify-between bg-white"
			style={{ padding: '2px' }}
			onSubmit={handleSubmit}
		>
			<label
				htmlFor="chatbot-input"
				className="w-4/5"
				style={{ padding: '2px 3px 2px 2px' }}
			>
				<span className="hidden">Type your message</span>
				<textarea
					data-testid="chat-form-input"
					id="chatbot-input"
					data-tag-name="input"
					maxLength={maxCharacterLength}
					className="chat-apperance-none block w-full px-4 pt-4 border-none"
					style={{ height: '52px' }}
					ref={inputRef}
					value={userInputValue}
					placeholder="Ask me a question..."
					onChange={handleInputChange}
					onKeyDown={(evt: React.KeyboardEvent<HTMLTextAreaElement>) =>
						handleInputKeyDown(evt)
					}
				/>
			</label>
			<Button
				variant="tertiary"
				icon
				data-tag-name="submit cta"
				data-testid="chat-form-cta"
				className="chat-form-cta w-1/5 bg-white rounded-l-none rounded-tr-none"
				type="submit"
			>
				<span className="sr-only">Send message</span>
				<SendMessageIcon
					className={classnames('ico w-6 h-6', {
						'text-gray-70': !userInputValue.length,
						'text-cta-action': userInputValue.length,
					})}
				/>
			</Button>
		</form>
	);
};

export default ChatForm;
